var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import React from 'react';
import ClassifiedEditForm from '../components/classified/ClassifiedEditForm';
import PickupLocation from '../components/classified/PickupLocation';
import useBrowserAuthMe from '../hooks/useBrowserAuthMe';
import useListMySalePoints from '../hooks/useListMySalePoints';
import { deleteClassified, listMyClassifieds, updateClassified } from '../utils/api/classifiedApi';
import { buildInitialEditValues, statusLabels, } from '../utils/classifiedFormOptions';
import { mapConditionToLabel } from '../utils/classifiedUtil';
import { humanTimeDiff } from '../utils/dateUtil';
/** The order statuses are grouped and displayed in. */
var STATUS_GROUP_ORDER = ['active', 'sold', 'inactive', 'outdated'];
/**
 * The old PHP controller redirected users with an excessive amount of
 * classifieds to the bulk management page, to protect the grouped
 * list view. The React page keeps that behaviour.
 * */
var BULK_MANAGEMENT_THRESHOLD = 800;
var groupByStatus = function (classifieds) { return (classifieds.reduce(function (groups, classified) {
    var nextGroups = __assign({}, groups);
    nextGroups[classified.status] = __spreadArray(__spreadArray([], (nextGroups[classified.status] || [])), [
        classified,
    ]);
    return nextGroups;
}, {})); };
var formatPrice = function (price) { return price + ",-"; };
var ClassifiedBook = function (_a) {
    var classified = _a.classified;
    return (React.createElement("section", { className: "object book" },
        React.createElement("div", { className: "book-cover" }, classified.bookImageUrl && React.createElement("img", { src: classified.bookImageUrl, alt: classified.bookTitle || '' })),
        React.createElement("h2", null,
            React.createElement("a", { href: "/bok/" + classified.bookIsbn }, classified.bookTitle)),
        React.createElement("p", null,
            "ISBN\u00A0",
            classified.bookIsbn)));
};
/**
 * "Mine annonser" — lists all of the signed-in
 * user's classifieds grouped by status, with
 * shortcuts to mark as sold, edit or delete them.
 * */
var MineClassifiedsPage = function (_a) {
    var onClassifiedDeleted = _a.onClassifiedDeleted;
    var user = useBrowserAuthMe().user;
    var salePoints = useListMySalePoints().salePoints;
    var _b = React.useState(null), classifieds = _b[0], setClassifieds = _b[1];
    var _c = React.useState(true), isLoading = _c[0], setIsLoading = _c[1];
    var _d = React.useState(null), loadError = _d[0], setLoadError = _d[1];
    var _e = React.useState(null), editingId = _e[0], setEditingId = _e[1];
    var _f = React.useState(null), editValues = _f[0], setEditValues = _f[1];
    var _g = React.useState(false), isSavingEdit = _g[0], setIsSavingEdit = _g[1];
    var _h = React.useState(null), editError = _h[0], setEditError = _h[1];
    var _j = React.useState(null), actionPendingId = _j[0], setActionPendingId = _j[1];
    var _k = React.useState(null), actionError = _k[0], setActionError = _k[1];
    React.useEffect(function () {
        var isMounted = true;
        listMyClassifieds()
            .then(function (nextClassifieds) {
            if (isMounted) {
                setClassifieds(nextClassifieds || []);
            }
        })
            .catch(function () {
            if (isMounted) {
                setLoadError('Kunne ikke hente annonsene dine. Last siden på nytt for å prøve igjen.');
            }
        })
            .finally(function () {
            if (isMounted) {
                setIsLoading(false);
            }
        });
        return function () {
            isMounted = false;
        };
    }, []);
    var replaceClassified = function (next) {
        setClassifieds(function (current) { return ((current || []).map(function (classified) { return (classified.id === next.id ? next : classified); })); });
    };
    var markAsSold = function (classified) {
        setActionError(null);
        setActionPendingId(classified.id);
        updateClassified(classified.id, { status: 'sold' })
            .then(replaceClassified)
            .catch(function () { return setActionError('Kunne ikke markere annonsen som solgt. Prøv igjen.'); })
            .finally(function () { return setActionPendingId(null); });
    };
    var removeClassified = function (classified) {
        if (!window.confirm("Slette annonsen for \"" + classified.bookTitle + "\"?")) {
            return;
        }
        setActionError(null);
        setActionPendingId(classified.id);
        deleteClassified(classified.id)
            .then(function () {
            setClassifieds(function (current) { return (current || []).filter(function (item) { return item.id !== classified.id; }); });
            if (onClassifiedDeleted) {
                onClassifiedDeleted(classified.id);
            }
        })
            .catch(function () { return setActionError('Kunne ikke slette annonsen. Prøv igjen.'); })
            .finally(function () { return setActionPendingId(null); });
    };
    var startEditing = function (classified) {
        setActionError(null);
        setEditError(null);
        setEditingId(classified.id);
        setEditValues(buildInitialEditValues(classified));
    };
    var cancelEditing = function () {
        setEditingId(null);
        setEditValues(null);
        setEditError(null);
    };
    var saveEdit = function (classifiedId, request) {
        setIsSavingEdit(true);
        setEditError(null);
        updateClassified(classifiedId, request)
            .then(function (next) {
            replaceClassified(next);
            cancelEditing();
        })
            .catch(function () { return setEditError('Kunne ikke lagre endringene. Prøv igjen.'); })
            .finally(function () { return setIsSavingEdit(false); });
    };
    if (isLoading) {
        return React.createElement("div", { className: "main-content" },
            React.createElement("h1", null, "Mine annonser"),
            React.createElement("p", null, "Laster annonsene dine..."));
    }
    if (loadError || !classifieds) {
        return React.createElement("div", { className: "main-content", role: "alert" },
            React.createElement("h1", null, "Mine annonser"),
            React.createElement("p", null, loadError));
    }
    if (classifieds.length > BULK_MANAGEMENT_THRESHOLD) {
        window.location.href = '/administrer_annonser';
        return React.createElement("div", { className: "main-content" },
            React.createElement("h1", null, "Mine annonser"),
            React.createElement("p", null, "Du har veldig mange annonser, og sendes til annonseadministrasjonen..."));
    }
    var groups = groupByStatus(classifieds);
    return React.createElement("div", { className: "main-content" },
        React.createElement("h1", null, "Mine annonser"),
        React.createElement("div", null, user && React.createElement("a", { className: "button", href: "/selger/" + user.id }, "Vis min offentlige selgerprofil")),
        actionError && React.createElement("p", { role: "alert", className: "form-error" }, actionError),
        classifieds.length === 0 && React.createElement("p", null, "Du har for \u00F8yeblikket ikke lagt ut noen annonser. Du kan legge ut en annonse ved \u00E5 s\u00F8ke opp boken du vil selge \u00F8verst p\u00E5 siden."),
        STATUS_GROUP_ORDER
            .filter(function (status) { return (groups[status] || []).length > 0; })
            .map(function (status) { return React.createElement("section", { key: status },
            React.createElement("h2", null, statusLabels[status]),
            React.createElement("ul", null, groups[status].map(function (classified) { return React.createElement("li", { className: "object composite", key: classified.id },
                React.createElement(ClassifiedBook, { classified: classified }),
                editingId === classified.id && editValues
                    ? React.createElement("div", { className: "object classified" },
                        React.createElement(ClassifiedEditForm, { values: editValues, salePoints: salePoints, isSaving: isSavingEdit, error: editError, onChange: setEditValues, onSubmit: function (request) { return saveEdit(classified.id, request); }, onCancel: cancelEditing }))
                    : React.createElement("div", { className: "object classified" },
                        React.createElement(PickupLocation, { classified: classified }),
                        React.createElement("strong", { className: "price" }, formatPrice(classified.price)),
                        React.createElement("div", { className: "vertical vertical-center" },
                            React.createElement("strong", { className: "condition" }, mapConditionToLabel(classified.condition)),
                            React.createElement("small", null,
                                "Sist oppdatert: ",
                                humanTimeDiff(classified.lastUpdated || classified.timeCreated || undefined, Date.now()))),
                        classified.status === 'sold'
                            ? React.createElement("button", { type: "button", className: "is-flat-link", disabled: true }, "Solgt")
                            : React.createElement("button", { type: "button", className: "standout has-wide-text", disabled: actionPendingId === classified.id, onClick: function () { return markAsSold(classified); } }, actionPendingId === classified.id ? 'Lagrer...' : 'Marker som solgt'),
                        React.createElement("button", { type: "button", disabled: actionPendingId === classified.id, onClick: function () { return startEditing(classified); } }, "Endre"),
                        React.createElement("button", { type: "button", className: "dangerous", disabled: actionPendingId === classified.id, onClick: function () { return removeClassified(classified); } }, "Slett"))); }))); }));
};
export default MineClassifiedsPage;
